import { useTranslations } from "@/contexts/I18nContext";

interface DownloadableImageProps {
  src: string;
  alt: string;
  className?: string;
  /** Suggested filename for the download. Defaults to the URL basename. */
  filename?: string;
}

/**
 * Image with a hover-revealed download button overlay. Mirrors the
 * Android post + chat detail download behavior (Android uses a
 * DownloadManager IntentService; on web we rely on the browser's
 * native `<a download>` API). Touch users tap once to reveal the
 * button via `:focus-within`.
 */
export function DownloadableImage({
  src,
  alt,
  className = "",
  filename,
}: DownloadableImageProps) {
  const t = useTranslations();

  // Pick a sane default filename. The download attribute on an <a> is
  // honored by browsers when the resource is same-origin or CORS-allowed;
  // Firebase Storage download URLs include a token but the browser will
  // strip the query string from the suggested filename automatically.
  const suggestedName = filename ?? src.split("/").pop()?.split("?")[0] ?? "image.jpg";

  return (
    <div className={`relative group ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <a
        href={src}
        download={suggestedName}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("common.downloadImage")}
        title={t("common.downloadImage")}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity hover:bg-black/70"
        onClick={(e) => e.stopPropagation()}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </a>
    </div>
  );
}
